import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import {
  CheckCircle2,
  MapPin,
  Utensils,
  Waves,
  Users,
  HeartHandshake,
  Car,
  ChevronLeft,
  ChevronRight,
  Star,
  Leaf,
  Heart,
  Moon,
  Sparkles,
  Shield,
  ArrowRight,
  ArrowUp,
  Quote,
  Calendar,
  ChevronDown,
  Sun,
  Coffee,
  Clock,
  Eye,
  Bed,
  HandHeart,
  Wind,
  Gem,
  RotateCcw,
  TreePine,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";

/* Unified icon wrapper for visual consistency */
const IconBadge = ({ icon: Icon, size = "md" }: { icon: LucideIcon; size?: "sm" | "md" }) => {
  const sizes = {
    sm: { container: "w-9 h-9", icon: "w-4 h-4" },
    md: { container: "w-10 h-10", icon: "w-[18px] h-[18px]" },
  };
  const s = sizes[size];
  return (
    <div className={`${s.container} rounded-full bg-primary/8 flex items-center justify-center shrink-0`}>
      <Icon className={`${s.icon} text-primary`} />
    </div>
  );
};

import heroImg from "@/assets/residency-hero-v2.jpg";
import communityImg from "@/assets/residency-community.jpg";
import detailImg from "@/assets/residency-detail.jpg";
import hotelEntrada from "@/assets/hotel-entrada.jpg";
import hotelPiscina1 from "@/assets/hotel-piscina-1.jpg";
import hotelPiscina2 from "@/assets/hotel-piscina-2.jpg";
import hotelSpa from "@/assets/hotel-spa.jpg";
import hotelJardin from "@/assets/hotel-jardin.jpg";
import hotelTerrazaPiscina from "@/assets/hotel-terraza-piscina.jpg";
import hotelTumbonas from "@/assets/hotel-tumbonas.jpg";
import hotelBuganvillas from "@/assets/hotel-buganvillas.jpg";
import hotelTerrazaArcos from "@/assets/hotel-terraza-arcos.jpg";
import meditacionMar from "@/assets/residency-meditacion-mar.jpg";

/* ═══════════════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════════════ */

const SPOTS = 10;

const LOCATIONS = [
  { src: hotelEntrada, alt: "Entrada principal del Hotel Bahía de Calvià" },
  { src: hotelPiscina1, alt: "Piscina exterior con vistas al hotel" },
  { src: hotelSpa, alt: "Spa interior con piscina climatizada" },
  { src: hotelBuganvillas, alt: "Terraza con buganvillas en flor" },
  { src: hotelTerrazaArcos, alt: "Terraza con arcos y vistas al jardín" },
  { src: hotelJardin, alt: "Zona de descanso junto a la piscina" },
];

/* ═══════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════ */

const Residency = () => {
  const { t } = useTranslation();
  const [tIdx, setTIdx] = useState(0);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [mobile, setMobile] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formPlan, setFormPlan] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const PLAN_OPTIONS = [t('residency.pricing.shared'), t('residency.pricing.private'), t('residency.pricing.suite')];

  const PLANS = [
    {
      name: t('residency.pricing.shared'),
      sub: t('residency.pricing.sharedSub'),
      price: "2.190",
      perDay: "730",
      features: [t('residency.pricing.sharedF1'), t('residency.pricing.sharedF2'), t('residency.pricing.sharedF3'), t('residency.pricing.sharedF4'), t('residency.pricing.sharedF5'), t('residency.pricing.sharedF6')],
      pop: false,
    },
    {
      name: t('residency.pricing.private'),
      sub: t('residency.pricing.privateSub'),
      price: "2.690",
      perDay: "897",
      features: [t('residency.pricing.privateF1'), t('residency.pricing.privateF2'), t('residency.pricing.privateF3'), t('residency.pricing.privateF4'), t('residency.pricing.privateF5'), t('residency.pricing.privateF6'), t('residency.pricing.privateF7')],
      pop: true,
    },
    {
      name: t('residency.pricing.suite'),
      sub: t('residency.pricing.suiteSub'),
      price: "3.190",
      perDay: "1.063",
      features: [t('residency.pricing.suiteF1'), t('residency.pricing.suiteF2'), t('residency.pricing.suiteF3'), t('residency.pricing.suiteF4'), t('residency.pricing.suiteF5'), t('residency.pricing.suiteF6'), t('residency.pricing.suiteF7')],
      pop: false,
    },
  ];

  const TESTIMONIALS = [
    { name: t('residency.testimonials.t1Name'), info: t('residency.testimonials.t1Info'), text: t('residency.testimonials.t1Text') },
    { name: t('residency.testimonials.t2Name'), info: t('residency.testimonials.t2Info'), text: t('residency.testimonials.t2Text') },
    { name: t('residency.testimonials.t3Name'), info: t('residency.testimonials.t3Info'), text: t('residency.testimonials.t3Text') },
    { name: t('residency.testimonials.t4Name'), info: t('residency.testimonials.t4Info'), text: t('residency.testimonials.t4Text') },
    { name: t('residency.testimonials.t5Name'), info: t('residency.testimonials.t5Info'), text: t('residency.testimonials.t5Text') },
    { name: t('residency.testimonials.t6Name'), info: t('residency.testimonials.t6Info'), text: t('residency.testimonials.t6Text') },
  ];

  const FAQS = [
    { q: t('residency.faq.q1'), a: t('residency.faq.a1') },
    { q: t('residency.faq.q2'), a: t('residency.faq.a2') },
    { q: t('residency.faq.q3'), a: t('residency.faq.a3') },
    { q: t('residency.faq.q4'), a: t('residency.faq.a4') },
    { q: t('residency.faq.q5'), a: t('residency.faq.a5') },
    { q: t('residency.faq.q6'), a: t('residency.faq.a6') },
    { q: t('residency.faq.q7'), a: t('residency.faq.a7') },
    { q: t('residency.faq.q8'), a: t('residency.faq.a8') },
    { q: t('residency.faq.q9'), a: t('residency.faq.a9') },
    { q: t('residency.faq.q10'), a: t('residency.faq.a10') },
    { q: t('residency.faq.q11'), a: t('residency.faq.a11') },
    { q: t('residency.faq.q12'), a: t('residency.faq.a12') },
  ];

  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const tVis = mobile ? 1 : 3;
  const tMax = Math.max(0, TESTIMONIALS.length - tVis);
  const tTotal = mobile ? TESTIMONIALS.length : Math.ceil(TESTIMONIALS.length / 3);
  const tCurrent = mobile ? tIdx + 1 : Math.floor(tIdx / 1) + 1;

  const go = (id: string) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  const selectPlanAndScroll = (planName: string) => {
    setFormPlan(planName);
    go("#reservar");
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);

    try {
      // Send via mailto as primary action
      const subject = encodeURIComponent(`Residencia Masshappy, ${formPlan || "Consulta"}`);
      const body = encodeURIComponent(
        `Nombre: ${formName}\nEmail: ${formEmail}\nTeléfono: ${formPhone || "No indicado"}\nOpción: ${formPlan || "No seleccionada"}\n\nMensaje:\n${formMessage || "Sin mensaje adicional"}`
      );
      window.location.href = `mailto:info@masshappy.com,german@helvetica.digital,regina@helvetica.digital?subject=${subject}&body=${body}`;

      toast({
        title: t('residency.form.successTitle'),
        description: t('residency.form.successDesc'),
      });

    } catch {
      toast({
        title: t('residency.form.errorTitle'),
        description: t('residency.form.errorDesc'),
        variant: "destructive",
      });
    } finally {
      setFormSubmitting(false);
    }
  };

  const CtaBtn = ({ label = "Quiero mi plaza", ghost = false, className = "", targetId = "#reservar" }: { label?: string; ghost?: boolean; className?: string; targetId?: string }) => (
    <Button
      size="lg"
      variant={ghost ? "ghost" : "default"}
      className={`rounded-full text-sm sm:text-base ${ghost ? "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10" : "bg-primary-foreground text-foreground hover:bg-primary-foreground/90 shadow-strong"} ${className}`}
      onClick={() => go(targetId)}
    >
      {label}
      {!ghost && <ArrowRight className="w-4 h-4 ml-1.5" />}
    </Button>
  );

  // All gallery images for lightbox
  const allGalleryImages = [
    { src: hotelTerrazaPiscina, alt: "Terraza con sombrillas junto a la piscina" },
    { src: hotelPiscina2, alt: "Piscina exterior al atardecer" },
    ...LOCATIONS,
  ];

  return (
    <div className="min-h-screen bg-background text-foreground font-poppins">
      <Helmet>
        <title>{t("residency.seo.title", { defaultValue: "La Residencia de la Felicidad · MassHappy" })}</title>
        <meta
          name="description"
          content={t("residency.seo.description", {
            defaultValue:
              "Tres días en Mallorca para parar de verdad. Grupo reducido, sesiones guiadas y descanso real en Peguera.",
          })}
        />
        <link rel="canonical" href="https://masshappy.com/retiro" />
        <meta property="og:title" content={t("residency.seo.title", { defaultValue: "La Residencia de la Felicidad · MassHappy" })} />
        <meta property="og:description" content={t("residency.seo.description", { defaultValue: "Tres días en Mallorca para parar de verdad. Grupo reducido, sesiones guiadas y descanso real en Peguera." })} />
        <meta property="og:url" content="https://masshappy.com/retiro" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: t("residency.seo.title", { defaultValue: "La Residencia de la Felicidad · MassHappy" }),
            serviceType: "Wellness retreat",
            description: t("residency.seo.description", {
              defaultValue:
                "Tres días en Mallorca para parar de verdad. Grupo reducido, sesiones guiadas y descanso real en Peguera.",
            }),
            url: "https://masshappy.com/retiro",
            provider: {
              "@type": "Organization",
              name: "MassHappy",
              url: "https://masshappy.com",
            },
            areaServed: {
              "@type": "Place",
              name: "Peguera, Calvià, Mallorca, España",
            },
            location: {
              "@type": "Place",
              name: "Hotel Bahía de Calvià",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Peguera",
                addressRegion: "Islas Baleares",
                addressCountry: "ES",
              },
            },
            offers: [
              { "@type": "Offer", name: t("residency.pricing.shared"), price: "2190", priceCurrency: "EUR", url: "https://masshappy.com/retiro#reservar" },
              { "@type": "Offer", name: t("residency.pricing.private"), price: "2690", priceCurrency: "EUR", url: "https://masshappy.com/retiro#reservar" },
              { "@type": "Offer", name: t("residency.pricing.suite"), price: "3190", priceCurrency: "EUR", url: "https://masshappy.com/retiro#reservar" },
            ],
          })}
        </script>
      </Helmet>


      {/* ═══════════════════════════════════════════
          NO INTERNAL HEADER, using global Header with transparency
          ═══════════════════════════════════════════ */}

      {/* ═══════════════════════════════════════════
          2 · HERO
          ═══════════════════════════════════════════ */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden">
        <img src={heroImg} alt="Terraza mediterránea al atardecer" className="absolute inset-0 w-full h-full object-cover scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(25_20%_8%/0.82)] via-[hsl(25_20%_12%/0.4)] to-[hsl(25_20%_16%/0.12)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(25_20%_8%/0.55)] via-transparent to-transparent" />

        <div className="relative z-10 w-full pt-28 pb-20">
          <div className="container mx-auto max-w-7xl px-4 sm:px-6">
            <div className="max-w-2xl space-y-5 sm:space-y-7">

              <p className="text-[11px] uppercase tracking-[0.35em] text-primary-foreground/60 font-medium">
                {t('residency.hero.tag')}
              </p>

              <h1 className="text-[2rem] sm:text-[3.5rem] md:text-[4rem] lg:text-[4.75rem] font-serif font-semibold text-primary-foreground leading-[1.08] tracking-tight">
                {t('residency.hero.h1Name')}
                <br />
                <span className="italic font-light opacity-90 text-[0.62em]">{t('residency.hero.h1Descriptor')}</span>
              </h1>

              <p className="font-serif text-lg sm:text-2xl text-primary-foreground/90 leading-snug max-w-lg">
                {t('residency.hero.headline1')} <span className="italic opacity-90">{t('residency.hero.headline2')}</span>
              </p>

              <p className="text-sm sm:text-base md:text-[1.125rem] text-primary-foreground/80 max-w-lg leading-[1.7]">
                <strong className="text-primary-foreground font-semibold">{t('residency.hero.threeDaysAndTwoNights')}</strong> {t('residency.hero.subtitle')}
              </p>


              <div className="flex flex-col sm:flex-row gap-3.5 pt-2">
                <CtaBtn label={t('residency.hero.cta')} />
                <CtaBtn label={t('residency.hero.ctaSecondary')} ghost className="px-6" targetId="#experiencia" />
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-3">
                {[
                  { icon: Calendar, text: t('residency.hero.trustDays') },
                  { icon: Users, text: t('residency.hero.trustGroup') },
                  { icon: MapPin, text: t('residency.hero.trustLocation') },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2">
                    <item.icon className="w-3.5 h-3.5 text-primary-foreground/50" />
                    <span className="text-[11px] uppercase tracking-[0.2em] text-primary-foreground/50 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          3 · VALUE STRIP
          ═══════════════════════════════════════════ */}
      <section className="py-10 md:py-14 bg-card border-b border-border/40">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-4">
            {[
              { icon: Calendar, title: t('residency.valueStrip.days'), desc: t('residency.valueStrip.daysDesc') },
              { icon: Users, title: t('residency.valueStrip.group'), desc: t('residency.valueStrip.groupDesc') },
              { icon: HeartHandshake, title: t('residency.valueStrip.facilitation'), desc: t('residency.valueStrip.facilitationDesc') },
              { icon: Bed, title: t('residency.valueStrip.hotel'), desc: t('residency.valueStrip.hotelDesc') },
              { icon: Utensils, title: t('residency.valueStrip.food'), desc: t('residency.valueStrip.foodDesc') },
            ].map((item, idx) => (
              <div key={item.title} className={`flex flex-col items-center text-center gap-2.5 ${idx === 4 ? "col-span-2 md:col-span-1" : ""}`}>
                <IconBadge icon={item.icon} />
                <div>
                  <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          4 · WHY THIS / WHY NOW
          ═══════════════════════════════════════════ */}
      <section id="experiencia" className="py-28 md:py-36 bg-background">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="grid md:grid-cols-2 gap-16 md:gap-20 items-center">
            
            <div className="space-y-8">
              <p className="text-[11px] uppercase tracking-[0.3em] text-primary font-medium">{t('residency.why.tag')}</p>
              <h2 className="text-3xl md:text-[2.75rem] font-serif font-semibold leading-[1.12]">
                {t('residency.why.headline1')}
                <br />
                <span className="italic font-light">{t('residency.why.headline2')}</span>
              </h2>
              <div className="space-y-5 text-muted-foreground leading-[1.8]">
                <p>{t('residency.why.p1')}</p>
                <p>{t('residency.why.p2')}</p>
                <p>{t('residency.why.p3')}</p>
              </div>
            </div>

            <div className="space-y-5">
              {[
                { title: t('residency.why.card1Title'), text: t('residency.why.card1Text') },
                { title: t('residency.why.card2Title'), text: t('residency.why.card2Text') },
                { title: t('residency.why.card3Title'), text: t('residency.why.card3Text') },
              ].map((item) => (
                <div key={item.title} className="bg-card rounded-xl p-6 border border-border/60 shadow-soft">
                  <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          5 · WHO THIS IS FOR
          ═══════════════════════════════════════════ */}
      <section className="py-28 md:py-36 bg-muted">
        <div className="container mx-auto max-w-6xl px-6">

          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[11px] uppercase tracking-[0.3em] text-primary font-medium mb-5">{t('residency.whoFor.tag')}</p>
            <h2 className="text-3xl md:text-[2.75rem] font-serif font-semibold leading-[1.12]">
              {t('residency.whoFor.headline1')}
              <br />
              <span className="italic font-light">{t('residency.whoFor.headline2')}</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { icon: HandHeart, title: t('residency.whoFor.item1Title'), text: t('residency.whoFor.item1Text') },
              { icon: Wind, title: t('residency.whoFor.item2Title'), text: t('residency.whoFor.item2Text') },
              { icon: Sparkles, title: t('residency.whoFor.item3Title'), text: t('residency.whoFor.item3Text') },
              { icon: RotateCcw, title: t('residency.whoFor.item4Title'), text: t('residency.whoFor.item4Text') },
              { icon: TreePine, title: t('residency.whoFor.item5Title'), text: t('residency.whoFor.item5Text') },
              { icon: Gem, title: t('residency.whoFor.item6Title'), text: t('residency.whoFor.item6Text') },
            ].map((item) => (
              <div key={item.title} className="bg-card rounded-xl p-6 border border-border/50 shadow-soft flex flex-col gap-3">
                <IconBadge icon={item.icon} />
                <h3 className="font-semibold text-foreground text-[0.95rem] leading-snug">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-14 space-y-6">
            <p className="text-muted-foreground italic max-w-md mx-auto leading-relaxed">
              {t('residency.whoFor.closingLine1')}
              <br />
              {t('residency.whoFor.closingLine2')}
            </p>
            <Button onClick={() => go("#reservar")} className="rounded-full px-8" size="lg">
              {t('residency.whoFor.cta')}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════
          6 · WHAT YOU'LL EXPERIENCE
          ═══════════════════════════════════════════ */}
      <section className="bg-background">
        <div className="grid md:grid-cols-2 min-h-[600px]">
          <div className="aspect-[4/3] md:aspect-auto overflow-hidden bg-muted">
            <img src={hotelTerrazaArcos} alt="Terraza del Hotel Bahía de Calvià" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="flex items-center px-8 py-20 md:px-16 lg:px-20">
            <div className="max-w-md space-y-6">
              <p className="text-xs uppercase tracking-[0.25em] text-primary">{t('residency.experience.tag')}</p>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold leading-snug">
                {t('residency.experience.headline1')}
                <br />
                <span className="italic font-light">{t('residency.experience.headline2')}</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {t('residency.experience.description')}
              </p>
              <ul className="space-y-3 text-sm text-foreground/75">
                {[
                  t('residency.experience.item1'),
                  t('residency.experience.item2'),
                  t('residency.experience.item3'),
                  t('residency.experience.item4'),
                  t('residency.experience.item5'),
                  t('residency.experience.item6'),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-3 gap-6 pt-6 border-t border-border/50">
                {[
                  { n: "3", l: t('residency.experience.days') },
                  { n: "2", l: t('residency.experience.nights') },
                  { n: "10", l: t('residency.experience.maxSpots') },
                ].map((s) => (
                  <div key={s.l} className="text-center">
                    <p className="text-3xl font-bold text-primary">{s.n}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          7 · OUTCOMES
          ═══════════════════════════════════════════ */}
      <section className="py-28 md:py-36 bg-muted">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <p className="text-[11px] uppercase tracking-[0.3em] text-primary font-medium mb-5">{t('residency.outcomes.tag')}</p>
            <h2 className="text-3xl md:text-[2.75rem] font-serif font-semibold leading-[1.12]">
              {t('residency.outcomes.headline1')}
              <br />
              <span className="italic font-light">{t('residency.outcomes.headline2')}</span>
            </h2>
            <p className="text-muted-foreground mt-5 max-w-lg mx-auto leading-relaxed">
              {t('residency.outcomes.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Emotional */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <IconBadge icon={Heart} size="sm" />
                <h3 className="font-semibold text-lg text-foreground">{t('residency.outcomes.emotional')}</h3>
              </div>
              {[
                { title: t('residency.outcomes.e1Title'), text: t('residency.outcomes.e1Text') },
                { title: t('residency.outcomes.e2Title'), text: t('residency.outcomes.e2Text') },
                { title: t('residency.outcomes.e3Title'), text: t('residency.outcomes.e3Text') },
              ].map((item) => (
                <div key={item.title} className="bg-card rounded-xl p-5 border border-border/50 shadow-soft">
                  <h4 className="font-medium text-foreground text-sm mb-1.5">{item.title}</h4>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>

            {/* Physical */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <IconBadge icon={Waves} size="sm" />
                <h3 className="font-semibold text-lg text-foreground">{t('residency.outcomes.physical')}</h3>
              </div>
              {[
                { title: t('residency.outcomes.p1Title'), text: t('residency.outcomes.p1Text') },
                { title: t('residency.outcomes.p2Title'), text: t('residency.outcomes.p2Text') },
                { title: t('residency.outcomes.p3Title'), text: t('residency.outcomes.p3Text') },
              ].map((item) => (
                <div key={item.title} className="bg-card rounded-xl p-5 border border-border/50 shadow-soft">
                  <h4 className="font-medium text-foreground text-sm mb-1.5">{item.title}</h4>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>

            {/* Mental */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <IconBadge icon={Sparkles} size="sm" />
                <h3 className="font-semibold text-lg text-foreground">{t('residency.outcomes.mental')}</h3>
              </div>
              {[
                { title: t('residency.outcomes.m1Title'), text: t('residency.outcomes.m1Text') },
                { title: t('residency.outcomes.m2Title'), text: t('residency.outcomes.m2Text') },
                { title: t('residency.outcomes.m3Title'), text: t('residency.outcomes.m3Text') },
              ].map((item) => (
                <div key={item.title} className="bg-card rounded-xl p-5 border border-border/50 shadow-soft">
                  <h4 className="font-medium text-foreground text-sm mb-1.5">{item.title}</h4>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA after outcomes */}
          <div className="text-center mt-16">
            <Button onClick={() => go("#reservar")} className="rounded-full px-8" size="lg">
              {t('residency.outcomes.cta')}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════
          8 · PROGRAM BY DAY
          ═══════════════════════════════════════════ */}
      <section id="programa" className="py-28 md:py-36 bg-background">
        <div className="container mx-auto max-w-6xl px-6">

          <div className="text-center max-w-2xl mx-auto mb-20">
            <p className="text-[11px] uppercase tracking-[0.3em] text-primary font-medium mb-5">{t('residency.program.tag')}</p>
            <h2 className="text-3xl md:text-[2.75rem] font-serif font-semibold leading-[1.12]">
              {t('residency.program.headline1')}
              <br />
              <span className="italic font-light">{t('residency.program.headline2')}</span>
            </h2>
            <p className="text-muted-foreground mt-5 max-w-lg mx-auto leading-relaxed">
              {t('residency.program.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Day 1 */}
            <div className="bg-card rounded-2xl border border-border/60 shadow-soft overflow-hidden flex flex-col">
              <div className="bg-primary/5 px-7 py-6 border-b border-border/40">
                <div className="flex items-center gap-3 mb-3">
                  <IconBadge icon={Moon} size="sm" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">{t('residency.program.day1Label')}</p>
                    <p className="text-xs text-muted-foreground">{t('residency.program.day1Sub')}</p>
                  </div>
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground leading-snug">{t('residency.program.day1Title')}</h3>
                <p className="text-xs text-primary/70 italic mt-1.5">{t('residency.program.day1Mood')}</p>
              </div>
              <div className="px-7 py-6 flex-1 space-y-3.5">
                {[
                  t('residency.program.day1i1'),
                  t('residency.program.day1i2'),
                  t('residency.program.day1i3'),
                  t('residency.program.day1i4'),
                ].map((m, j) => (
                  <div key={j} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0 mt-2" />
                    <p className="text-sm text-foreground/75 leading-relaxed">{m}</p>
                  </div>
                ))}
              </div>
              <div className="px-7 pb-6">
                <p className="text-[11px] text-muted-foreground/60 uppercase tracking-wider">{t('residency.program.day1Footer')}</p>
              </div>
            </div>

            {/* Day 2 */}
            <div className="bg-card rounded-2xl border-2 border-primary/20 shadow-medium overflow-hidden flex flex-col relative">
              <div className="absolute top-3 right-3">
                <span className="text-[10px] uppercase tracking-wider bg-primary/10 text-primary font-semibold px-2.5 py-1 rounded-full">{t('residency.program.day2Badge')}</span>
              </div>
              <div className="bg-primary/5 px-7 py-6 border-b border-border/40">
                <div className="flex items-center gap-3 mb-3">
                  <IconBadge icon={Sun} size="sm" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">{t('residency.program.day2Label')}</p>
                    <p className="text-xs text-muted-foreground">{t('residency.program.day2Sub')}</p>
                  </div>
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground leading-snug">{t('residency.program.day2Title')}</h3>
                <p className="text-xs text-primary/70 italic mt-1.5">{t('residency.program.day2Mood')}</p>
              </div>
              <div className="px-7 py-6 flex-1 space-y-3.5">
                {[
                  t('residency.program.day2i1'),
                  t('residency.program.day2i2'),
                  t('residency.program.day2i3'),
                  t('residency.program.day2i4'),
                  t('residency.program.day2i5'),
                  t('residency.program.day2i6'),
                ].map((m, j) => (
                  <div key={j} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0 mt-2" />
                    <p className="text-sm text-foreground/75 leading-relaxed">{m}</p>
                  </div>
                ))}
              </div>
              <div className="px-7 pb-6">
                <p className="text-[11px] text-muted-foreground/60 uppercase tracking-wider">{t('residency.program.day2Footer')}</p>
              </div>
            </div>

            {/* Day 3 */}
            <div className="bg-card rounded-2xl border border-border/60 shadow-soft overflow-hidden flex flex-col">
              <div className="bg-primary/5 px-7 py-6 border-b border-border/40">
                <div className="flex items-center gap-3 mb-3">
                  <IconBadge icon={Coffee} size="sm" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">{t('residency.program.day3Label')}</p>
                    <p className="text-xs text-muted-foreground">{t('residency.program.day3Sub')}</p>
                  </div>
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground leading-snug">{t('residency.program.day3Title')}</h3>
                <p className="text-xs text-primary/70 italic mt-1.5">{t('residency.program.day3Mood')}</p>
              </div>
              <div className="px-7 py-6 flex-1 space-y-3.5">
                {[
                  t('residency.program.day3i1'),
                  t('residency.program.day3i2'),
                  t('residency.program.day3i3'),
                  t('residency.program.day3i4'),
                ].map((m, j) => (
                  <div key={j} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0 mt-2" />
                    <p className="text-sm text-foreground/75 leading-relaxed">{m}</p>
                  </div>
                ))}
              </div>
              <div className="px-7 pb-6">
                <p className="text-[11px] text-muted-foreground/60 uppercase tracking-wider">{t('residency.program.day3Footer')}</p>
              </div>
            </div>
          </div>

          {/* Protocol note */}
          <div className="mt-12 bg-muted border border-border rounded-xl px-6 py-5 max-w-2xl mx-auto">
            <div className="flex items-start gap-3.5">
              <IconBadge icon={Shield} size="sm" />
              <div className="space-y-2">
                <p className="text-sm text-foreground/80">
                  <span className="font-medium text-foreground">{t('residency.program.protocolTitle')}</span>{" "}
                  {t('residency.program.protocolText')}
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════
          9 · WHAT'S INCLUDED
          ═══════════════════════════════════════════ */}
      <section className="py-28 md:py-36 bg-muted">
        <div className="container mx-auto max-w-6xl px-6">

          <div className="text-center max-w-2xl mx-auto mb-20">
            <p className="text-[11px] uppercase tracking-[0.3em] text-primary font-medium mb-5">{t('residency.included.tag')}</p>
            <h2 className="text-3xl md:text-[2.75rem] font-serif font-semibold leading-[1.12]">
              {t('residency.included.headline1')}
              <br />
              <span className="italic font-light">{t('residency.included.headline2')}</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            {[
              { icon: Bed, title: t('residency.included.nights'), desc: t('residency.included.nightsDesc') },
              { icon: Utensils, title: t('residency.included.food'), desc: t('residency.included.foodDesc') },
              { icon: Heart, title: t('residency.included.sessions'), desc: t('residency.included.sessionsDesc') },
              { icon: Users, title: t('residency.included.group'), desc: t('residency.included.groupDesc') },
              { icon: Waves, title: t('residency.included.access'), desc: t('residency.included.accessDesc') },
              { icon: Leaf, title: t('residency.included.protocol'), desc: t('residency.included.protocolDesc') },
              { icon: Car, title: t('residency.included.transfers'), desc: t('residency.included.transfersDesc') },
              { icon: Sparkles, title: t('residency.included.integration'), desc: t('residency.included.integrationDesc') },
            ].map((item) => (
              <div key={item.title} className="bg-card rounded-xl p-5 border border-border/50 shadow-soft space-y-2.5">
                <IconBadge icon={item.icon} size="sm" />
                <h3 className="font-semibold text-sm text-foreground">{item.title}</h3>
                <p className="text-[13px] text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('residency.included.fromPrice')}
            </p>
            <Button onClick={() => go("#inversion")} className="rounded-full px-8" size="lg">
              {t('residency.included.ctaOptions')}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>

          {/* Who this is NOT for */}
          <div className="mt-24 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground font-medium mb-4">{t('residency.notFor.tag')}</p>
              <h3 className="text-2xl md:text-3xl font-serif font-semibold leading-snug">
                {t('residency.notFor.headline')} <span className="italic font-light">{t('residency.notFor.headlineSuffix')}</span>
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                t('residency.notFor.item1'),
                t('residency.notFor.item2'),
                t('residency.notFor.item3'),
                t('residency.notFor.item4'),
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 bg-card rounded-lg px-5 py-4 border border-border/40">
                  <span className="text-muted-foreground/40 text-sm mt-0.5">✕</span>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-muted-foreground/70 mt-6 italic max-w-md mx-auto">
              {t('residency.notFor.closing')}
            </p>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════
          10 · SETTING
          ═══════════════════════════════════════════ */}
      <section id="hotel" className="py-28 md:py-36 bg-background">
        <div className="container mx-auto max-w-6xl px-6">

          <div className="grid md:grid-cols-2 gap-14 lg:gap-20 items-center mb-20">
            <div className="space-y-7">
              <p className="text-[11px] uppercase tracking-[0.3em] text-primary font-medium">{t('residency.setting.tag')}</p>
              <h2 className="text-3xl md:text-[2.75rem] font-serif font-semibold leading-[1.12]">
                {t('residency.setting.headline1')}
                <br />
                <span className="italic font-light">{t('residency.setting.headline2')}</span>
              </h2>
              <div className="space-y-4 text-muted-foreground leading-[1.8]">
                <p>{t('residency.setting.p1')}</p>
                <p>
                  {t('residency.setting.p2')} <em className="text-foreground">{t('residency.setting.p2emphasis')}</em>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                {[
                  { label: t('residency.setting.textures'), sub: t('residency.setting.texturesSub') },
                  { label: t('residency.setting.light'), sub: t('residency.setting.lightSub') },
                  { label: t('residency.setting.silence'), sub: t('residency.setting.silenceSub') },
                  { label: t('residency.setting.scents'), sub: t('residency.setting.scentsSub') },
                ].map((d) => (
                  <div key={d.label} className="bg-muted rounded-lg px-4 py-3">
                    <p className="text-sm font-medium text-foreground">{d.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{d.sub}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl overflow-hidden aspect-[3/4] bg-muted cursor-pointer" onClick={() => setLightboxIdx(0)}>
                <img src={hotelTerrazaPiscina} alt="Terraza con sombrillas junto a la piscina" className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
              </div>
              <div className="rounded-xl overflow-hidden aspect-[3/4] mt-8 bg-muted cursor-pointer" onClick={() => setLightboxIdx(1)}>
                <img src={hotelPiscina2} alt="Piscina exterior al atardecer" className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {LOCATIONS.map((img, i) => (
              <div
                key={i}
                className={`rounded-xl overflow-hidden bg-muted cursor-pointer ${i === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-[4/3]"}`}
                onClick={() => setLightboxIdx(i + 2)}
              >
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-x-10 gap-y-4 mt-12 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> {t('residency.setting.locationTag')}</span>
            <span className="flex items-center gap-2"><Waves className="w-4 h-4 text-primary" /> {t('residency.setting.beachDistance')}</span>
            <span className="flex items-center gap-2"><Car className="w-4 h-4 text-primary" /> {t('residency.setting.airportDistance')}</span>
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> {t('residency.setting.privateHotel')}</span>
          </div>

          <div className="text-center mt-14">
            <Button onClick={() => go("#reservar")} className="rounded-full px-8" size="lg">
              {t('residency.setting.ctaPlace')}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>

        </div>
      </section>

      {/* Image Lightbox */}
      <Dialog open={lightboxIdx !== null} onOpenChange={() => setLightboxIdx(null)}>
        <DialogContent className="max-w-4xl h-[80vh] p-0 border-0 bg-black/95">
          <div className="relative w-full h-full flex items-center justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
              onClick={() => setLightboxIdx(null)}
            >
              <X className="w-6 h-6" />
            </Button>

            {lightboxIdx !== null && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 z-10 text-white hover:bg-white/20"
                  onClick={() => setLightboxIdx(lightboxIdx === 0 ? allGalleryImages.length - 1 : lightboxIdx - 1)}
                >
                  <ChevronLeft className="w-8 h-8" />
                </Button>

                <img
                  src={allGalleryImages[lightboxIdx]?.src}
                  alt={allGalleryImages[lightboxIdx]?.alt}
                  className="max-w-full max-h-full object-contain"
                />

                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-14 z-10 text-white hover:bg-white/20"
                  onClick={() => setLightboxIdx(lightboxIdx === allGalleryImages.length - 1 ? 0 : lightboxIdx + 1)}
                >
                  <ChevronRight className="w-8 h-8" />
                </Button>

                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full text-sm">
                  {lightboxIdx + 1} / {allGalleryImages.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════
          PRICING
          ═══════════════════════════════════════════ */}
      <section id="inversion" className="py-28 bg-muted">
        <div className="container mx-auto max-w-6xl px-6">
          <p className="text-center text-xs uppercase tracking-[0.25em] text-primary mb-6">{t('residency.pricing.tag')}</p>
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-center mb-4">
            {t('residency.pricing.headline')}
          </h2>
          <p className="text-center text-muted-foreground mb-3 max-w-md mx-auto">
            {t('residency.pricing.description')}
          </p>
          <p className="text-center text-xs text-primary font-medium mb-14">
            {t('residency.pricing.spotsLeft')}
          </p>

          <div className="grid md:grid-cols-3 gap-5">
            {PLANS.map((p, i) => (
              <Card key={i} className={`relative overflow-hidden transition-all duration-300 hover:shadow-medium ${p.pop ? "border-primary ring-2 ring-primary/15 md:scale-[1.03]" : "border-border"}`}>
                {p.pop && (
                  <div className="absolute top-0 inset-x-0 bg-primary text-primary-foreground text-[11px] font-medium text-center py-1.5 uppercase tracking-wider">
                    {t('residency.pricing.mostChosen')}
                  </div>
                )}
                <CardContent className={`p-7 flex flex-col h-full ${p.pop ? "pt-11" : ""}`}>
                  <div className="mb-5">
                    <h3 className="text-lg font-bold">{p.name}</h3>
                    <p className="text-xs text-muted-foreground">{p.sub}</p>
                  </div>
                  <div className="mb-1">
                    <span className="text-3xl sm:text-4xl font-bold tracking-tight">€{p.price}</span>
                  </div>
                   <p className="text-[10px] sm:text-[11px] text-muted-foreground mb-6 break-words">
                    {t('residency.pricing.perPerson')} · €{p.perDay}/{t('residency.pricing.perDay')} · {t('residency.pricing.threeDaysTwoNights')}
                  </p>
                  <ul className="space-y-2.5 flex-1 mb-8">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span className="text-foreground/75">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full rounded-full text-sm" variant={p.pop ? "default" : "outline"} onClick={() => selectPlanAndScroll(p.name)}>
                    {p.pop ? t('residency.pricing.chooseThis') : t('residency.pricing.bookThis')}
                    {p.pop && <ArrowRight className="w-4 h-4 ml-1 shrink-0" />}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-10 text-center space-y-2">
            <div className="inline-flex items-center gap-2 text-sm text-foreground/70">
              <Shield className="w-4 h-4 text-primary" />
              <span>{t('residency.pricing.refundPolicy')}</span>
            </div>
            <p className="text-xs text-muted-foreground">{t('residency.pricing.safePayment')}</p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          11 · TRUST & SOCIAL PROOF
          ═══════════════════════════════════════════ */}
      <section className="py-28 md:py-36 bg-background">
        <div className="container mx-auto max-w-6xl px-6">

          <div className="text-center max-w-2xl mx-auto mb-20">
            <p className="text-[11px] uppercase tracking-[0.3em] text-primary font-medium mb-5">{t('residency.trust.tag')}</p>
            <h2 className="text-3xl md:text-[2.75rem] font-serif font-semibold leading-[1.12]">
              {t('residency.trust.headline1')}
              <br />
              <span className="italic font-light">{t('residency.trust.headline2')}</span>
            </h2>
          </div>

          {/* Experience principles */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
            {[
              { icon: Shield, title: t('residency.trust.principle1Title'), desc: t('residency.trust.principle1Desc') },
              { icon: Users, title: t('residency.trust.principle2Title'), desc: t('residency.trust.principle2Desc') },
              { icon: Eye, title: t('residency.trust.principle3Title'), desc: t('residency.trust.principle3Desc') },
              { icon: Heart, title: t('residency.trust.principle4Title'), desc: t('residency.trust.principle4Desc') },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-card rounded-xl p-6 border border-border/50 shadow-soft space-y-3">
                  <IconBadge icon={Icon} size="sm" />
                  <h3 className="font-semibold text-[0.95rem] text-foreground">{item.title}</h3>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Trust stats */}
          <div className="flex flex-wrap items-center justify-center gap-10 mb-16 py-8 border-y border-border/40">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {["A", "L", "M", "C", "E"].map((l, i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-primary/10 border-2 border-background flex items-center justify-center text-[11px] font-semibold text-primary">{l}</div>
                ))}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{t('residency.trust.participants')}</p>
                <p className="text-[11px] text-muted-foreground">{t('residency.trust.participantsSub')}</p>
              </div>
            </div>
            <div className="text-center">
              <div className="flex items-center gap-1 justify-center">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{t('residency.trust.rating')}</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-foreground">100%</p>
              <p className="text-[11px] text-muted-foreground">{t('residency.trust.wouldRepeatSub')}</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-foreground">{t('residency.trust.editions')}</p>
              <p className="text-[11px] text-muted-foreground">{t('residency.trust.editionsSub')}</p>
            </div>
          </div>

          {/* Testimonials */}
          <div>
            <p className="text-center text-[11px] uppercase tracking-[0.3em] text-muted-foreground font-medium mb-10">{t('residency.trust.testimonialsTag')}</p>
            <div className="relative">
              <div className="grid md:grid-cols-3 gap-5">
                {TESTIMONIALS.slice(tIdx, tIdx + tVis).map((t, i) => (
                  <Card key={`${tIdx}-${i}`} className="border-border/50 bg-card">
                    <CardContent className="p-7 space-y-5">
                      <Quote className="w-6 h-6 text-primary/15" />
                      <p className="text-foreground/75 leading-relaxed text-[15px]">"{t.text}"</p>
                      <div className="flex items-center gap-3 pt-1">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs">{t.name[0]}</div>
                        <div>
                          <p className="font-medium text-sm">{t.name}</p>
                          <p className="text-[11px] text-muted-foreground">{t.info}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Carousel controls + dots */}
              <div className="flex flex-col items-center gap-4 mt-10">
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="icon" className="rounded-full" disabled={tIdx === 0} onClick={() => setTIdx((p) => Math.max(0, p - 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>

                  {/* Pagination dots */}
                  <div className="flex items-center gap-1.5">
                    {Array.from({ length: mobile ? TESTIMONIALS.length : Math.ceil(TESTIMONIALS.length / 3) }).map((_, i) => {
                      const isActive = mobile ? i === tIdx : Math.floor(tIdx / 3) === i || (i === Math.ceil(TESTIMONIALS.length / 3) - 1 && tIdx >= tMax);
                      return (
                        <button
                          key={i}
                          className={`rounded-full transition-all duration-300 ${isActive ? "w-6 h-2 bg-primary" : "w-2 h-2 bg-border hover:bg-primary/30"}`}
                          onClick={() => setTIdx(mobile ? i : Math.min(i * 3, tMax))}
                          aria-label={`Ver testimonios ${i + 1}`}
                        />
                      );
                    })}
                  </div>

                  <Button variant="outline" size="icon" className="rounded-full" disabled={tIdx >= tMax} onClick={() => setTIdx((p) => Math.min(tMax, p + 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                {/* Mobile indicator */}
                {mobile && (
                  <p className="text-[11px] text-muted-foreground">
                    {tIdx + 1} de {TESTIMONIALS.length}
                  </p>
                )}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════
          12 · FAQ
          ═══════════════════════════════════════════ */}
      <section className="py-28 md:py-36 bg-muted">
        <div className="container mx-auto max-w-3xl px-6">
          <div className="text-center mb-16">
            <p className="text-[11px] uppercase tracking-[0.3em] text-primary font-medium mb-5">{t('residency.faq.tag')}</p>
            <h2 className="text-3xl md:text-[2.75rem] font-serif font-semibold leading-[1.12]">
              {t('residency.faq.headline1')}
              <br />
              <span className="italic font-light">{t('residency.faq.headline2')}</span>
            </h2>
          </div>
          <div className="space-y-2.5">
            {FAQS.map((faq, i) => (
              <div key={i} className="border border-border/60 rounded-xl overflow-hidden bg-card">
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/30 transition-colors"
                >
                  <span className="font-medium text-sm pr-4">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform duration-300 ${faqOpen === i ? "rotate-180" : ""}`} />
                </button>
                <div
                  className="grid transition-all duration-300 ease-out"
                  style={{
                    gridTemplateRows: faqOpen === i ? "1fr" : "0fr",
                  }}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{faq.a}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10 space-y-4">
            <Button onClick={() => go("#reservar")} className="rounded-full px-8" size="lg">
              {t('residency.faq.cta')}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <p className="text-sm text-muted-foreground">{t('residency.faq.moreQuestions')}</p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          13 · RESERVATION FORM
          ═══════════════════════════════════════════ */}
      <section id="reservar" className="py-28 bg-background">
        <div className="container mx-auto max-w-5xl px-6">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div className="space-y-6">
              <p className="text-xs uppercase tracking-[0.25em] text-primary">{t('residency.form.tag')}</p>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold leading-snug">
                {t('residency.form.headline1')}
                <br />
                <span className="italic font-light">{t('residency.form.headline2')}</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {t('residency.form.description')}
              </p>

              <div className="space-y-3 text-sm text-foreground/65">
                <div className="flex items-center gap-2.5">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span>{t('residency.form.date')}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span>{t('residency.form.location')}</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Users className="w-4 h-4 text-primary" />
                  <span dangerouslySetInnerHTML={{ __html: t('residency.form.spotsLeft') }} />
                </div>
              </div>

              <div className="pt-4 border-t border-border/50 space-y-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Shield className="w-3.5 h-3.5 text-primary" />
                  {t('residency.form.refund')}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('residency.form.responseTime')}
                </p>
              </div>
            </div>

            <Card className="border-border/60 shadow-soft">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-2">{t('residency.form.formTitle')}</h3>
                <p className="text-sm text-muted-foreground mb-6">{t('residency.form.formSubtitle')}</p>
                <form
                  ref={formRef}
                  className="space-y-4"
                  onSubmit={handleFormSubmit}
                >
                  <Input
                    placeholder={t('residency.form.name')}
                    required
                    className="rounded-lg"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                  />
                  <Input
                    type="email"
                    placeholder={t('residency.form.email')}
                    required
                    className="rounded-lg"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                  />
                  <Input
                    type="tel"
                    placeholder={t('residency.form.phone')}
                    className="rounded-lg"
                    value={formPhone}
                    onChange={(e) => setFormPhone(e.target.value)}
                  />
                  <div>
                    <label className="text-xs text-muted-foreground mb-1.5 block">{t('residency.form.planLabel')}</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {PLAN_OPTIONS.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setFormPlan(opt)}
                          className={`text-xs border rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
                            formPlan === opt
                              ? "border-primary bg-primary/5 text-primary font-medium"
                              : "border-border hover:border-primary hover:bg-primary/5"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Textarea
                    placeholder={t('residency.form.message')}
                    rows={3}
                    className="rounded-lg"
                    value={formMessage}
                    onChange={(e) => setFormMessage(e.target.value)}
                  />
                  <Button type="submit" className="w-full rounded-full" size="lg" disabled={formSubmitting}>
                    {formSubmitting ? t('residency.form.submitting') : t('residency.form.submit')}
                    {!formSubmitting && <ArrowRight className="w-4 h-4 ml-1.5" />}
                  </Button>
                  <p className="text-[11px] text-center text-muted-foreground">{t('residency.form.mailtoNote')}</p>
                  <p className="text-[11px] text-center text-muted-foreground">{t('residency.form.confirmFooter')}</p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════ */}
      <footer className="bg-card border-t border-border/50 py-12">
        <div className="container mx-auto max-w-7xl px-6 text-center space-y-4">
          <div className="flex items-center justify-center gap-2.5">
            <img src="/lovable-uploads/76ebc92c-2231-4c7b-98bf-254356462622.png" alt="Masshappy" className="h-6 w-auto" />
            <span className="text-primary font-poppins font-semibold">masshappy</span>
          </div>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto leading-relaxed">
            {t('residency.footer.description')}
          </p>
          <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground/70">
            <Link to="/privacidad" className="hover:text-foreground transition-colors">{t('residency.footer.privacy')}</Link>
            <span>·</span>
            <Link to="/terminos" className="hover:text-foreground transition-colors">{t('residency.footer.terms')}</Link>
            <span>·</span>
            <a href="mailto:info@masshappy.com" className="hover:text-foreground transition-colors">info@masshappy.com</a>
          </div>
          <p className="text-[11px] text-muted-foreground/60">
            &copy; {new Date().getFullYear()} masshappy · {t('residency.footer.rights')}
          </p>
        </div>
      </footer>

      {/* Scroll to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`fixed bottom-6 right-6 z-50 w-11 h-11 rounded-full bg-card border border-border/60 shadow-medium flex items-center justify-center text-foreground/60 hover:text-foreground hover:shadow-strong transition-all duration-300 ${
          showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        aria-label={t('residency.scrollTop')}
      >
        <ArrowUp className="w-4 h-4" />
      </button>
    </div>
  );
};

export default Residency;
